import React from 'react';

import { FormikProps } from 'formik';

// components
import Divider from '../../../components/shared/Divider';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

// hooks
import useFormulaTimingSlots from '../../../hooks/timing-slots/useTimingSlots';

// model
import {
    FormulaDetailModel,
    FormulaSchedulingOptions,
    FormulaViewSetupFormValue,
} from '../../../models/formula.model';

import DeliveryTypeInfoSection from './DeliveryTypeInfoSection';
import MessageTimingConstraints from './MessageTimingConstraints';
import StartStopInput from './StartStopInput';

interface AutomatedDeliveryWithConstraintsSectionProps {
    isDisabled?: boolean;
    isControlledFormula: boolean;
    start: Date | null;
    setStart: (startDate: Date | null) => void;
    stop: Date | null;
    setStop: (endDate: Date | null) => void;
    formula?: FormulaDetailModel;
    schedulingType?: FormulaSchedulingOptions;
    formikProps?: FormikProps<FormulaViewSetupFormValue>;
}

const AutomatedDeliveryWithConstraintsSection: React.FC<
    React.PropsWithChildren<AutomatedDeliveryWithConstraintsSectionProps>
> = ({
    isDisabled,
    isControlledFormula,
    start,
    setStart,
    stop,
    setStop,
    formula,
    schedulingType,
    formikProps,
}) => {
    const { data: timingSlots } = useFormulaTimingSlots();

    return (
        <Container>
            <DeliveryTypeInfoSection
                title="Messaging optimized within limits you set"
                description="Select the start date and expiration date, permissible time slots, or both. Aampe’s AI will work within these limitations. Set as few constraints as possible to give the AI room to learn and act on individual preferences."
            />
            <BodyContainer>
                <CalenderContainer>
                    Select a start, a expire date or both
                    <StartStopInput
                        isDisabled={isDisabled || isControlledFormula}
                        start={start}
                        setStart={setStart}
                        stop={stop}
                        setStop={setStop}
                    />
                </CalenderContainer>
                <Divider type="vertical" />
                <CalenderContainer>
                    <SectionLabelRow>
                        Select time slots
                        <TimezoneTag>Recipient&apos;s local time</TimezoneTag>
                    </SectionLabelRow>
                    {timingSlots?.length && (
                        <MessageTimingConstraints
                            timingSlots={timingSlots as any}
                            formula={formula}
                            isDisabled={isDisabled}
                            schedulingType={schedulingType}
                            formik={formikProps}
                        />
                    )}
                </CalenderContainer>
            </BodyContainer>
        </Container>
    );
};

export default AutomatedDeliveryWithConstraintsSection;

const Container = styled.div({
    display: `flex`,
    alignItems: `start`,
    flexDirection: 'column',
    gap: aampeTheme.spacing(3),
    width: '100%',
});

const BodyContainer = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing(10),
    width: '100%',
});

const CalenderContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(2),
});

const SectionLabelRow = styled.div({
    display: 'flex',
    alignItems: 'baseline',
    gap: aampeTheme.spacing4(1),
});

const TimezoneTag = styled.span({
    fontSize: '11px',
    fontWeight: 400,
    color: aampeTheme.colors.solidWG01,
    lineHeight: '16px',
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
});
